import React, { useState, useEffect, useCallback, useRef } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useLang } from '../../context/LangContext';
import { useBidContext } from '../../context/BidContext';
import { getTerms, preBid, getPreBidDetail } from '../../lib/api/user/userApi';
import { PreBidDetailData } from '../../lib/api/user/userApi.types';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import { FormTitle } from '../../customComponents/FormTitle';
import { CustomDropdown } from '../../customComponents/CustomDropdown';
import AgreementButton from '../../components/AgreementButton';
import TermsConsentSheet from '../../components/TermsConsentSheet';
import AnimatedBorderButton from '../../components/AnimatedBorderButton';
import NoDataContainer from '../../components/NoDataContainer';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 140px; /* 고정 버튼 영역을 위한 하단 패딩 */
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px 0;
  gap: 24px;
`;

// 정보 섹션
const InfoSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 32px 0;
  margin-bottom: 8px;
  border-top: 1px solid #f3f3f3;
  border-bottom: 1px solid #f3f3f3;
`;

const LotImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
  background: #f0f0f0;
`;

const LotInfoContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  justify-content: flex-start;
`;

const LotName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #000;
  line-height: 1.4;
  text-align: left;
  align-self: flex-start;
`;

const LotId = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: 500;
  text-align: left;
  align-self: flex-start;
`;

// 금액 섹션
const AmountSection = styled.div`
  background: white;
  border-radius: 12px;
  padding-top: 20px;
  margin-bottom: 8px;
`;

const DropdownContainer = styled.div`
  margin-bottom: 20px;
`;

const FeeCalculationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const FeeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #262626;
`;

const FeeDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e9ecef;
  margin: 12px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #7a6647;
  padding-top: 8px;
`;

// 툴팁 관련 스타일
const TooltipContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const QuestionIcon = styled.div.attrs<{}, { ref?: React.Ref<HTMLDivElement> }>(() => ({}))`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #666666;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #333333;
  }
`;

const Tooltip = styled.div<{ 
  visible: boolean; 
  $left: string; 
  $transform: string; 
  $arrowLeft: string; 
}>`
  position: absolute;
  bottom: 100%;
  left: ${props => props.$left};
  transform: ${props => props.$transform};
  background-color: #333333;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: opacity 0.2s, visibility 0.2s;
  margin-bottom: 4px;
  min-width: 280px; /* 최소 너비 설정 */
  text-align: center;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: ${props => props.$arrowLeft};
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: #333333;
  }
`;

// 드롭다운 옵션 스타일 컴포넌트들
const DropdownOptionContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const CurrencyText = styled.span`
  color: #b6a488;
  font-size: 12px;
  font-weight: 500;
`;

const AmountText = styled.span`
  color: #7a6647;
  font-size: 16px;
  font-weight: 600;
`;

// 입찰 유의사항 스타일
const BiddingNoticeContainer = styled.div`
  background: #f8f5f0;
  border-radius: 8px;
  padding: 16px;
  min-height: 137px; /* 최소 높이로 변경 */
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const BiddingNoticeTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin: 0;
`;

const InfoIcon = styled.svg`
  width: 20px;
  height: 20px;
  fill: #666666;
  flex-shrink: 0;
`;

const BiddingNoticeList = styled.ul`
  margin: 0;
  padding: 0 0 0 12px; /* 왼쪽에 12px 여백 추가 */
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BiddingNoticeItem = styled.li`
  font-size: 14px;
  color: #666666;
  line-height: 1.4;
  position: relative;
  padding-left: 16px; /* 점과 텍스트 사이 간격 줄임 */
  text-align: left; /* 명시적으로 왼쪽 정렬 */
  
  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #999999;
  }
`;

// 동의 섹션 스타일
const AgreementSectionWrapper = styled.div`
  background: white;
  border-radius: 12px;
  margin-bottom: 8px;
`;

const AgreementItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ViewButton = styled.button`
  background: none;
  border: none;
  color: #666666;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    color: #333333;
  }
`;

// 고정 버튼 컨테이너
const FixedButtonContainer = styled.div`
  position: fixed;
  bottom: 75px; /* 앱에서 하단 네비게이션 위로 */
  left: 0;
  right: 0;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  z-index: 999;
  border-top: 1px solid #e9ecef;

  @media (min-width: 768px) {
    bottom: 0; /* 웹에서는 항상 바닥에 고정 */
    padding: 20px 24px;
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;
  

  @media (min-width: 768px) {
    max-width: 1200px;
  }
`;

const bidRequestTexts = {
  ko: {
    title: 'LOT 입찰 신청',
    infoSection: '경매 정보',
    amountSection: '입찰가',
    bidAmountLabel: '입찰가',
    bidAmountPlaceholder: '입찰가를 선택해주세요',
    auctionHouseFee: '경매장 수수료',
    onlineAuctionFee: '온라인경매 수수료',
    feeVAT: '수수료 부가세',
    feeVATTooltip: '경매장 수수료와 온라인경매 수수료의 합산에 대한 부가세입니다',
    totalAmount: '총 합계',
    currency: 'KRW',
    agreementSection: '동의사항',
    allAgreeTitle: '전체 동의',
    auctionTermsTitle: '경매장 이용약관 동의',
    auctionTermsDetailTitle: '경매장 이용약관 상세',
    agreeButton: '동의',
    bidButton: '꾹 눌러 입찰 신청',
    bidButtonTooltip: '1초간 꾹 눌러서 입찰',
    biddingNoticeTitle: '입찰 시 유의사항',
    biddingNoticeItem1: '라이브 경매 중에는 금액 수정이 불가합니다.',
    biddingNoticeItem2: '직접·메일 입찰 금액 동일 시, 시간 순 우선 처리됩니다.',
    biddingNoticeItem3: '경매 수수료는 경매 별로 상이합니다.'
  },
  en: {
    title: 'LOT Bid Request',
    infoSection: 'Auction Information',
    amountSection: 'Bid Price',
    bidAmountLabel: 'Bid Price',
    bidAmountPlaceholder: 'Please select bid price',
    auctionHouseFee: 'Auction House Fee',
    onlineAuctionFee: 'Online Auction Fee',
    feeVAT: 'Fee VAT',
    feeVATTooltip: 'VAT on the combined auction house and online auction fees',
    totalAmount: 'Total Amount',
    currency: 'USD',
    agreementSection: 'Agreements',
    allAgreeTitle: 'Agree All',
    auctionTermsTitle: 'Agree to Auction Terms',
    auctionTermsDetailTitle: 'Auction Terms Details',
    agreeButton: 'Agree',
    bidButton: 'Hold to Submit Bid',
    bidButtonTooltip: 'Hold for 1 second to bid',
    biddingNoticeTitle: 'Bidding Precautions',
    biddingNoticeItem1: 'Amount modification is not possible during live auction.',
    biddingNoticeItem2: 'When direct and mail bid amounts are the same, they are processed in time order.',
    biddingNoticeItem3: 'Auction fees vary by auction.'
  }
};

function BidRequest() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLang();
  const { currentLotData, setCurrentLotData } = useBidContext(); // Context에서 데이터 가져오기
  const texts = bidRequestTexts[lang];
  
  // 상태 관리
  const [auctioneerTerms, setAuctioneerTerms] = useState<string>('');
  const [isLoadingTerms, setIsLoadingTerms] = useState(false);

  // 기본 auctioneer 약관 (API에서 가져올 수 없을 때만 사용)
  const getDefaultAuctioneerTerms = useCallback(() => {
    const auctioneerName = currentLotData?.auctioneer?.name || '경매장';
    
    return `
      <h3>${auctioneerName} 이용약관</h3>
      <p>약관 정보를 불러올 수 없습니다. 잠시 후 다시 시도해주세요.</p>
    `;
  }, [currentLotData]);
  
  // Context 데이터가 없을 때 location.state에서 lotId를 가져와서 API 호출
  useEffect(() => {
    const fetchLotDataIfNeeded = async () => {
      devLog('🔍 [BidRequest] fetchLotDataIfNeeded 시작:', {
        hasCurrentLotData: !!currentLotData,
        locationState: location.state,
        lotId: location.state?.lotId
      });
      
      if (!currentLotData) {
        // location.state에서 lotId 가져오기
        const lotId = location.state?.lotId;
        
        if (lotId) {
          try {
            devLog('🔍 [BidRequest] Context 데이터 없음, API에서 데이터 가져오기:', lotId);
            const response = await getPreBidDetail(lotId);
            devLog('🔍 [BidRequest] API 응답:', response.data);
            
            if (response.data.statusCode === 200 && response.data.data) {
              const lotData: any = response.data.data; // LOT 데이터가 직접 반환됨
              
              // API 응답을 PreBidDetailData 형식에 맞게 변환
              const transformedLotData: PreBidDetailData = {
                index: lotData.index,
                code: lotData.code || `LOT.${lotData.index}`, // API에서 code 필드 사용, 없으면 fallback
                name: lotData.name, // API에서 name 필드로 직접 반환
                description: lotData.description || '',
                images: lotData.images || [],
                startPrice: lotData.startPrice || 0,
                exchangeRate: lotData.exchangeRate || 1400,
                startPriceUsd: lotData.startPriceUsd || 0,
                recommendNo: lotData.recommendNo || null,
                likedUserIndexes: lotData.likedUserIndexes || [],
                likeCount: lotData.likeCount || 0,
                createBy: lotData.createBy || '',
                updateBy: lotData.updateBy || '',
                createAt: lotData.createAt || '',
                updateAt: lotData.updateAt || '',
                category: lotData.category || { code: '', name: '', krName: '' },
                subCategory: lotData.subCategory || { code: '', name: '', krName: '' },
                auctioneer: {
                  index: lotData.auctioneer?.index || 0,
                  name: lotData.auctioneer?.name || '',
                  description: lotData.auctioneer?.description || '',
                  profilePic: lotData.auctioneer?.profilePic || '',
                  term: lotData.auctioneer?.term
                },
                auction: {
                  index: lotData.auction?.index || 0,
                  name: lotData.auction?.name || '',
                  description: lotData.auction?.description || '',
                  url: lotData.auction?.url || '',
                  auctionLotNo: lotData.auction?.auctionLotNo || 0,
                  preBidStartAt: lotData.auction?.preBidStartAt || '',
                  preBidEndAt: lotData.auction?.preBidEndAt || '',
                  auctionStartAt: lotData.auction?.auctionStartAt || '',
                  auctionEndAt: lotData.auction?.auctionEndAt || '',
                  paymentDueAt: lotData.auction?.paymentDueAt || '',
                  auctionFeeRate: lotData.auction?.auctionFeeRate || 0,
                  platformFeeRate: lotData.auction?.platformFeeRate || 0,
                  createAt: lotData.auction?.createAt || '',
                  updateAt: lotData.auction?.updateAt || null,
                  biddingRanges: lotData.auction?.biddingRanges || []
                },
                preBids: lotData.preBids || []
              };
              
              setCurrentLotData(transformedLotData);
              devLog('🔍 [BidRequest] API에서 데이터 로드 완료:', transformedLotData);
            } else {
              devError('🔍 [BidRequest] API 응답 실패:', response.data);
              toast.error(`LOT 정보를 불러올 수 없습니다. (상태코드: ${response.data.statusCode})`);
              navigate(-1);
            }
          } catch (error) {
            devError('🔍 [BidRequest] LOT 정보 조회 실패:', error);
            const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류';
            toast.error(`LOT 정보를 불러오는 중 오류가 발생했습니다: ${errorMessage}`);
            navigate(-1);
          }
        } else {
          toast.error('LOT 정보를 찾을 수 없습니다.');
          navigate(-1);
        }
        return;
      }
    };

    fetchLotDataIfNeeded();
  }, [currentLotData, location.state, setCurrentLotData, navigate]);

  // auctioneer 약관 로드
  useEffect(() => {
    if (!currentLotData) {
      return; // currentLotData가 없으면 아무것도 하지 않음
    }

    // auctioneer의 term 데이터가 있으면 바로 사용, 없으면 API에서 조회
    if (currentLotData.auctioneer?.term) {
      setAuctioneerTerms(currentLotData.auctioneer.term);
      setIsLoadingTerms(false);
    } else {
      // auctioneer별 약관 조회 (fallback)
      const fetchAuctioneerTerms = async () => {
        try {
          setIsLoadingTerms(true);
          const response = await getTerms({ termLang: lang === 'ko' ? 'KOR' : 'ENG' });
          
          if (response.data.statusCode === 200 && response.data.data?.terms) {
            // auctioneer에 특화된 약관을 찾거나 기본 약관 사용
            const auctionTerms = response.data.data.terms.find(
              term => term.termType === 'AUCTION' || term.termType === 'AUCTIONEER'
            );
            
            if (auctionTerms) {
              setAuctioneerTerms(auctionTerms.termContent || getDefaultAuctioneerTerms());
            } else {
              setAuctioneerTerms(getDefaultAuctioneerTerms());
            }
          } else {
            setAuctioneerTerms(getDefaultAuctioneerTerms());
          }
        } catch (err) {
          devError('약관 조회 실패:', err);
          setAuctioneerTerms(getDefaultAuctioneerTerms());
        } finally {
          setIsLoadingTerms(false);
        }
      };

      fetchAuctioneerTerms();
    }
  }, [currentLotData, navigate, lang, getDefaultAuctioneerTerms]);
  
  // 동적 입찰가 옵션 생성 함수
  const generateBidPriceOptions = useCallback(() => {
    if (!currentLotData?.auction?.biddingRanges || !currentLotData?.exchangeRate) {
      return [];
    }

    // preBids에서 가장 높은 금액 찾기, 없으면 startPrice 사용
    let maxBidPrice = 0;
    
    if (currentLotData.preBids && currentLotData.preBids.length > 0) {
      // preBids가 있는 경우: preBids에서 가장 높은 금액 사용
      maxBidPrice = currentLotData.preBids.reduce((max, bid) => {
        return Math.max(max, bid.price);
      }, 0);
    } else {
      // preBids가 없는 경우: startPrice를 기준으로 사용
      maxBidPrice = currentLotData.startPrice || 0;
    }

    // 해당 금액이 속하는 구간 찾기
    const biddingRanges = currentLotData.auction.biddingRanges;
    let currentRange = biddingRanges.find(range => {
      const minAmount = range.minBidAmount;
      const maxAmount = range.maxBidAmount;
      
      return maxBidPrice >= minAmount && (maxAmount === null || maxBidPrice < maxAmount);
    });

    // 구간을 찾지 못했거나 maxBidPrice가 구간의 최대값과 같은 경우, 다음 구간을 찾기
    if (!currentRange) {
      // maxBidPrice가 어떤 구간의 maxAmount와 정확히 일치하는 경우 다음 구간 찾기
      const rangeIndex = biddingRanges.findIndex(range => 
        range.maxBidAmount !== null && maxBidPrice === range.maxBidAmount
      );
      
      if (rangeIndex !== -1 && rangeIndex + 1 < biddingRanges.length) {
        // 다음 구간이 존재하면 사용
        currentRange = biddingRanges[rangeIndex + 1];
      } else {
        // 마지막 구간이거나 구간을 찾을 수 없는 경우
        currentRange = biddingRanges[biddingRanges.length - 1];
      }
    }

    if (!currentRange) {
      // 방어적 코딩: 가장 마지막 구간(상한이 없는 구간)을 사용
      currentRange = biddingRanges[biddingRanges.length - 1];
      
      if (!currentRange) {
        // 구간이 아예 없는 경우 (이론적으로는 발생하지 않아야 함)
        const defaultPriceKrw = maxBidPrice + 1000; // maxBidPrice 기준으로 기본값 설정
        const defaultPriceUsd = Math.round((defaultPriceKrw / currentLotData.exchangeRate) * 100) / 100;
        
        return [
          { 
            value: defaultPriceKrw.toString(), 
            content: (
              <DropdownOptionContainer>
                <CurrencyText>{texts.currency}</CurrencyText>
                <AmountText>{lang === 'ko' ? defaultPriceKrw.toLocaleString() : `${defaultPriceUsd.toFixed(2)}`}</AmountText>
              </DropdownOptionContainer>
            )
          }
        ];
      }
    }

    // 시작 금액: 최대 금액 + rate
    const startAmount = maxBidPrice + currentRange.rate;
    const rate = currentRange.rate;
    const exchangeRate = currentLotData.exchangeRate;

    // 최대 10개까지 옵션 생성
    const options = [];
    for (let i = 0; i < 10; i++) {
      const priceKrw = startAmount + (rate * i);
      const priceUsd = Math.round((priceKrw / exchangeRate) * 100) / 100; // 소수점 2자리까지
      
      options.push({
        value: priceKrw.toString(), // 값은 항상 KRW 기준으로 저장
        content: (
          <DropdownOptionContainer>
            <CurrencyText>{texts.currency}</CurrencyText>
            <AmountText>
              {lang === 'ko' 
                ? priceKrw.toLocaleString() 
                : `${priceUsd.toFixed(2)}`
              }
            </AmountText>
          </DropdownOptionContainer>
        )
      });
    }

    return options;
  }, [currentLotData, texts.currency, lang]);

  // 입찰가 옵션들
  const bidPriceOptions = generateBidPriceOptions();

  const [selectedBidPrice, setSelectedBidPrice] = useState(''); // 기본값을 빈 문자열로 초기화
  const [isIndividualAgreed, setIsIndividualAgreed] = useState(false);
  const [showTermsSheet, setShowTermsSheet] = useState(false);
  const [viewedSection, setViewedSection] = useState('auction-terms');
  const [showVATTooltip, setShowVATTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ left: '50%', transform: 'translateX(-50%)', arrowLeft: '50%' });
  const questionIconRef = useRef<HTMLDivElement>(null);

  // 툴팁 위치 계산 함수
  const calculateTooltipPosition = useCallback(() => {
    if (!questionIconRef.current) return;
    
    const iconRect = questionIconRef.current.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const tooltipWidth = 300; // 예상 툴팁 너비
    
    // 아이콘이 화면 왼쪽에서 가까운 경우
    if (iconRect.left < tooltipWidth / 2) {
      setTooltipPosition({
        left: '0px',
        transform: 'none',
        arrowLeft: `${iconRect.width / 2}px`
      });
    }
    // 아이콘이 화면 오른쪽에서 가까운 경우
    else if (iconRect.right > windowWidth - tooltipWidth / 2) {
      setTooltipPosition({
        left: 'auto',
        transform: 'translateX(-100%)',
        arrowLeft: `${tooltipWidth - iconRect.width / 2}px`
      });
    }
    // 일반적인 경우 (중앙 정렬)
    else {
      setTooltipPosition({
        left: '50%',
        transform: 'translateX(-50%)',
        arrowLeft: '50%'
      });
    }
  }, []);

  // 입찰가 옵션이 생성되면 첫 번째 옵션을 기본값으로 설정
  useEffect(() => {
    if (bidPriceOptions.length > 0 && !selectedBidPrice) {
      setSelectedBidPrice(bidPriceOptions[0].value);
    }
  }, [bidPriceOptions, selectedBidPrice]);

  // 툴팁 외부 클릭 시 닫기 및 위치 계산
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showVATTooltip) {
        setShowVATTooltip(false);
      }
    };

    // 툴팁이 표시될 때 위치 계산
    if (showVATTooltip) {
      calculateTooltipPosition();
      // 화면 크기 변경 시에도 위치 재계산
      window.addEventListener('resize', calculateTooltipPosition);
    }

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('resize', calculateTooltipPosition);
    };
  }, [showVATTooltip, calculateTooltipPosition]);

  // 디버깅 로그 추가
  devLog('🔍 [BidRequest] currentLotData 상태:', {
    currentLotData,
    hasCurrentLotData: !!currentLotData,
    currentLotDataKeys: currentLotData ? Object.keys(currentLotData) : null
  });

  // API에서 받았다고 가정하는 수수료율 - 실제 API 데이터 사용
  const feeRates = {
    auctionHouseFeeRate: currentLotData?.auction?.auctionFeeRate || 20, // API에서 가져온 수수료율
    onlineAuctionFeeRate: currentLotData?.auction?.platformFeeRate || 5, // API에서 가져온 플랫폼 수수료율
    vatRate: 10 // 10% (고정)
  };

  // 수수료 계산 함수
  const calculateFees = (bidPrice: number) => {
    const auctionHouseFee = bidPrice * (feeRates.auctionHouseFeeRate / 100);
    const onlineAuctionFee = bidPrice * (feeRates.onlineAuctionFeeRate / 100);
    const totalFees = auctionHouseFee + onlineAuctionFee;
    const feeVAT = totalFees * (feeRates.vatRate / 100);
    const totalAmount = bidPrice + totalFees + feeVAT;

    return {
      auctionHouseFee: Math.floor(auctionHouseFee),
      onlineAuctionFee: Math.floor(onlineAuctionFee),
      feeVAT: Math.floor(feeVAT),
      totalAmount: Math.floor(totalAmount)
    };
  };

  const formatNumber = (value: number) => {
    if (lang === 'ko') {
      return value.toLocaleString();
    } else {
      // USD 변환
      const exchangeRate = currentLotData?.exchangeRate || 1409.09;
      const usdValue = Math.round((value / exchangeRate) * 100) / 100;
      return usdValue.toFixed(2);
    }
  };

  // 이미지 URL 처리 함수
  const getImageSrc = (imageUrl?: string | null) => {
    if (!imageUrl || imageUrl.trim() === "") {
      return "/common/no_image.svg";
    }
    
    // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
    if (imageUrl.startsWith("http") || imageUrl.startsWith("blob:")) {
      return imageUrl;
    }
    
    // 절대 경로인 경우 (/로 시작)
    if (imageUrl.startsWith("/")) {
      return `${process.env.REACT_APP_API_HOST}${imageUrl}`;
    }
    
    // 단순 파일명인 경우 (서버에서 받은 파일명)
    return `${process.env.REACT_APP_API_HOST}/${imageUrl}`;
  };

  // 약관 섹션 데이터 - auctioneerTerms 사용
  const termsSection = {
    key: 'auction-terms',
    title: texts.auctionTermsDetailTitle,
    content: auctioneerTerms || getDefaultAuctioneerTerms()
  };

  const handleViewTerms = () => {
    setShowTermsSheet(true);
  };

  const handleCloseTermsSheet = () => {
    setShowTermsSheet(false);
  };

  const handleSectionClick = (sectionKey: string) => {
    setViewedSection(sectionKey);
  };

  const isFormValid = () => {
    return selectedBidPrice && isIndividualAgreed;
  };

  const handleBidSubmit = async () => {
    if (!currentLotData?.index || !selectedBidPrice) {
      toast.error('입찰 정보가 올바르지 않습니다.');
      return;
    }

    try {
      // 사전 입찰 API 호출
      const response = await preBid(currentLotData.index, {
        price: parseInt(selectedBidPrice)
      });

      if (response.data.statusCode === 200 || response.data.statusCode === 201) {
        // 성공 토스트 메시지
        toast.success('입찰 신청이 완료되었습니다!');
        
        // 성공 후 이전 페이지로 돌아가기
        setTimeout(() => {
          navigate(-1);
        }, 1000);
      } else {
        // API에서 에러 메시지가 있으면 표시, 없으면 기본 메시지
        toast.error(response.data.message || '입찰 신청에 실패했습니다.');
      }
    } catch (error: any) {
      devError('입찰 신청 실패:', error);
      
      // 에러 응답에서 메시지 추출
      let errorMessage = '입찰 신청에 실패했습니다.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleIncomplete = () => {
    // 완료되지 않았을 때의 처리
    devLog('입찰 신청이 완료되지 않았습니다');
  };

  const handleDisabled = () => {
    // 비활성화 상태일 때의 처리
    toast.warning('모든 정보를 입력하고 동의해주세요');
  };

  const Layout = (
    <MainLayout currentPage="home">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="home"
          title={texts.title}
          showBackButton={true}
          onBack={() => navigate(-1)}
        />
        <Container>
          <ContentContainer>
            {/* 정보 섹션 */}
            <InfoSection>
              <LotImage 
                src={currentLotData?.images?.[0] ? getImageSrc(currentLotData.images[0]) : "/common/no_image.svg"} 
                alt="Lot Image"
                onError={(e) => {
                  e.currentTarget.src = '/common/no_image.svg';
                }}
              />
              <LotInfoContainer>
                <LotName>{currentLotData?.name || 'LOT 정보를 불러올 수 없습니다.'}</LotName>
                <LotId>{currentLotData?.code || `LOT.${currentLotData?.index || '---'}`}</LotId>
              </LotInfoContainer>
            </InfoSection>

            {/* 금액 섹션 */}
            <div>
              <FormTitle>{texts.amountSection}</FormTitle>
              <AmountSection>
                <DropdownContainer>
                  <CustomDropdown
                    value={selectedBidPrice}
                    onChange={setSelectedBidPrice}
                    options={bidPriceOptions}
                    placeholder={
                      <DropdownOptionContainer>
                        <CurrencyText>-</CurrencyText>
                        <AmountText>{texts.bidAmountPlaceholder}</AmountText>
                      </DropdownOptionContainer>
                    }
                  />
                </DropdownContainer>
                {selectedBidPrice && (
                  <FeeCalculationContainer>
                    {(() => {
                      const bidPrice = parseInt(selectedBidPrice);
                      const fees = calculateFees(bidPrice);
                      return (
                        <>
                          <FeeRow>
                            <span>{texts.auctionHouseFee} ({feeRates.auctionHouseFeeRate}%)</span>
                            <span>{texts.currency} {formatNumber(fees.auctionHouseFee)}</span>
                          </FeeRow>
                          <FeeRow>
                            <span>{texts.onlineAuctionFee} ({feeRates.onlineAuctionFeeRate}%)</span>
                            <span>{texts.currency} {formatNumber(fees.onlineAuctionFee)}</span>
                          </FeeRow>
                          <FeeRow>
                            <TooltipContainer>
                              <span>{texts.feeVAT} ({feeRates.vatRate}%)</span>
                              <QuestionIcon 
                                ref={questionIconRef}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowVATTooltip(!showVATTooltip);
                                }}
                              >
                                ?
                              </QuestionIcon>
                              <Tooltip 
                                visible={showVATTooltip}
                                $left={tooltipPosition.left}
                                $transform={tooltipPosition.transform}
                                $arrowLeft={tooltipPosition.arrowLeft}
                              >
                                {texts.feeVATTooltip}
                              </Tooltip>
                            </TooltipContainer>
                            <span>{texts.currency} {formatNumber(fees.feeVAT)}</span>
                          </FeeRow>
                          <FeeDivider />
                          <TotalRow>
                            <span>{texts.totalAmount}</span>
                            <span>{texts.currency} {formatNumber(fees.totalAmount)}</span>
                          </TotalRow>
                        </>
                      );
                    })()}
                  </FeeCalculationContainer>
                )}
              </AmountSection>
            </div>

            {/* 동의 섹션 */}
            <div>
              {/* 입찰 시 유의사항 */}
              <BiddingNoticeContainer>
                <BiddingNoticeTitle>
                  <InfoIcon viewBox="0 0 24 24">
                    <path d="M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2ZM13,17H11V11H13V17ZM13,9H11V7H13V9Z" />
                  </InfoIcon>
                  {texts.biddingNoticeTitle}
                </BiddingNoticeTitle>
                <BiddingNoticeList>
                  <BiddingNoticeItem>{texts.biddingNoticeItem1}</BiddingNoticeItem>
                  <BiddingNoticeItem>{texts.biddingNoticeItem2}</BiddingNoticeItem>
                  <BiddingNoticeItem>{texts.biddingNoticeItem3}</BiddingNoticeItem>
                </BiddingNoticeList>
              </BiddingNoticeContainer>
              
              <AgreementSectionWrapper>
                <AgreementItem>
                  <AgreementButton
                    isClicked={isIndividualAgreed}
                    onClicked={() => setIsIndividualAgreed(!isIndividualAgreed)}
                  >
                    {texts.auctionTermsTitle}
                  </AgreementButton>
                  <ViewButton onClick={handleViewTerms}>
                    보기
                  </ViewButton>
                </AgreementItem>
              </AgreementSectionWrapper>
            </div>
          </ContentContainer>
          
          {/* 고정 버튼 영역 */}
          <FixedButtonContainer>
            <ButtonWrapper>
              <AnimatedBorderButton
                title={texts.bidButton}
                tooltipText={texts.bidButtonTooltip}
                onPressed={handleBidSubmit}
                onIncomplete={handleIncomplete}
                disabledCallback={handleDisabled}
                duration={1}
                height={48}
                borderRadius={16}
                borderThickness={3}
                backgroundColor="#A6D4D1"
                finalBorderColor="#22c55e"
                textColor="#262626"
                textPressedColor="#FFFFFF"
                textCompletedColor="#FFFFFF"
                disabled={!isFormValid()}
                hideTooltip={showTermsSheet} // 바텀시트가 열릴 때 툴팁 숨기기
              />
            </ButtonWrapper>
          </FixedButtonContainer>
        </Container>
        
        {/* 약관 동의서 시트 */}
        {showTermsSheet && (
          <TermsConsentSheet
            sections={[termsSection]}
            viewedSection={viewedSection}
            onClose={handleCloseTermsSheet}
            onSectionClick={handleSectionClick}
            buttonText={isLoadingTerms ? '로딩 중...' : texts.agreeButton}
            onButtonClick={() => {
              if (!isLoadingTerms) {
                setIsIndividualAgreed(true);
                handleCloseTermsSheet();
              }
            }}
          />
        )}
      </CenteredLayout>
    </MainLayout>
  );

  return (
    <ResponsiveView
      mobileView={Layout}
      desktopView={Layout}
    />
  );
}

export default BidRequest;
